import React, { ForwardRefRenderFunction } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { InvoiceFormTypes } from "models/formTypes";
import { InvoicePreviewFormProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import { CurrencyInputProps, DateInputProps, TextInputProps } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import {
  CurrencyInput,
  DateInput,
  FileUploader,
  GroupSection,
  TextAreaInput,
  VerticalFormField,
  WintForm,
  useWintForm,
} from "components/common/wintForms";
import useInvoicePreviewFormHelpers from "./useInvoicePreviewFormHelpers";
import InvoiceTemplatePreview from "../pdfPreview/templatePreview";
import InvoicePreviewSendOptionSection from "./sendMailOptions/previewSendOptionSection";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const InvoicePreviewForm: ForwardRefRenderFunction<FormHandles, InvoicePreviewFormProps> = (props, ref) => {
  const {
    invoicePut,
    invoiceId,
    serialNumber,
    ocr,
    status,
    fromRecurring,
    recurringInvoiceVersion,
    recurringId,
    currency,
    isReminder,
    defaultValues,
    validationSchema,
    version,
    canSendLastVersionToPagero,
    warnings,
    handleSubmit,
    reminderFeesUpdateHandler,
    onInvoiceSendMethodSelected,
  } = props;

  const { wintFormProps, connect, values, setValues } = useWintForm<InvoiceFormTypes.Preview>({
    formRef: ref,
    defaultValues,
    validationSchema,
    handleSubmit,
  });

  const { getFormattedMessage } = useInvoicePreviewFormHelpers();

  const reminderDateProps = connect("reminderDate");
  const reminderCostProps = connect("reminderCost");

  const onReminderDateChanged = (value: any, shouldValidate?: boolean) => {
    const dueDays = DateHelper.getDiffDays(DateHelper.today(), value);
    const emailSubject = getFormattedMessage(values.sendMail?.emailSubject ?? "", dueDays);
    const emailMessage = getFormattedMessage(values.sendMail?.emailMessage ?? "", dueDays);

    setValues(
      {
        ...values,
        reminderDate: value,
        sendMail: {
          ...values.sendMail,
          emailSubject: emailSubject,
          emailMessage: emailMessage,
        },
        notes: emailMessage,
      },
      shouldValidate
    );
  };

  const onReminderFeesChanged = (e: React.ChangeEvent<any>) => {
    const updatedValue = e.target.value;
    reminderCostProps.setValue(updatedValue);
    reminderFeesUpdateHandler && reminderFeesUpdateHandler(updatedValue ?? 0);
  };

  const showAttachmentsUpload = React.useMemo(() => {
    return values.isInvoiceDraft || isReminder;
  }, [values]);

  return (
    <WintForm {...wintFormProps}>
      <InvoiceTemplatePreview
        invoicePut={invoicePut}
        values={values}
        status={status}
        invoiceId={invoiceId}
        serialNumber={serialNumber}
        ocr={ocr}
        fromRecurring={fromRecurring}
        recurringInvoiceVersion={recurringInvoiceVersion}
        recurringId={recurringId}
        isReminder={isReminder}
        version={version}
      />
      {isReminder && (
        <GroupSection header={TranslationKeys.Invoice.Remind}>
          <VerticalFormField<DateInputProps>
            Component={DateInput}
            {...reminderDateProps}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReminderDate)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.ReminderDateDescription
            )}
            minDate={DateHelper.today()}
            setValue={onReminderDateChanged}
          />
          {!values.reminderCostHidden && (
            <VerticalFormField<CurrencyInputProps>
              Component={CurrencyInput}
              {...reminderCostProps}
              label={TranslationKeys.Invoice.ReminderCost}
              descriptionText={LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Invoice.ReminderCostDescription
              )}
              currency={currency}
              onChange={onReminderFeesChanged}
            />
          )}
        </GroupSection>
      )}
      <InvoicePreviewSendOptionSection
        invoiceId={invoiceId}
        connect={connect}
        values={values}
        setValues={setValues}
        version={version}
        invoicePut={invoicePut}
        isReminder={isReminder}
        status={status}
        canSendLastVersionToPagero={canSendLastVersionToPagero}
        warnings={warnings}
        onInvoiceSendMethodSelected={onInvoiceSendMethodSelected}
        nextInvoiceSerialNumber={props.nextInvoiceSerialNumber}
      />
      {showAttachmentsUpload && (
        <GroupSection
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MessagesAndAttachments)}
          className={cssExports.noBorder}
        >
          <VerticalFormField<TextInputProps>
            Component={TextAreaInput}
            {...connect("notes")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Message)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MessageDescription)}
          />
          <VerticalFormField
            Component={FileUploader}
            {...connect("attachments")}
            label={TranslationKeys.Common.Attachments}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.AttachmentsDescription
            )}
            singleSelection={false}
            hidden={isReminder}
          />
        </GroupSection>
      )}
    </WintForm>
  );
};

export default React.forwardRef(InvoicePreviewForm);
